const API = process.env.REACT_APP_BACKEND_URL || '';

export async function fetchContent() {
  // Cache-bust with a timestamp so no intermediate CDN / browser cache can
  // ever serve a stale copy when we poll/refresh after an admin save.
  const res = await fetch(`${API}/api/content?_=${Date.now()}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (!res.ok) throw new Error('Failed to fetch content');
  return res.json();
}

export async function adminLogin(password) {
  const res = await fetch(`${API}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Login failed');
  }
  return res.json();
}

export async function verifyToken(token) {
  const res = await fetch(`${API}/api/admin/verify`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.ok;
}

export async function updateContent(token, content) {
  const res = await fetch(`${API}/api/content`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(content),
  });
  if (!res.ok) throw new Error('Failed to update content');
  return res.json();
}

export async function updateSection(token, section, data) {
  const res = await fetch(`${API}/api/content/${section}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update section');
  return res.json();
}

/**
 * Upload file using chunked upload to bypass proxy body size limits (413).
 * Splits file into 4MB chunks, uploads each, then assembles on server.
 * @param {string} token - JWT token
 * @param {File} file - File object to upload
 * @param {function} onProgress - Callback (percent: number) called during upload
 * @returns {Promise<{success: boolean, url: string, filename: string, size: number}>}
 */
export async function uploadFile(token, file, onProgress) {
  const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB per chunk (well under proxy limits)
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  // For small files (< 4MB), use single chunk to keep it simple
  if (totalChunks <= 1) {
    return uploadFileSingle(token, file, onProgress);
  }

  let uploadedChunks = 0;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);

    const formData = new FormData();
    formData.append('file', blob, `chunk_${i}`);
    formData.append('upload_id', uploadId);
    formData.append('chunk_index', String(i));
    formData.append('total_chunks', String(totalChunks));

    const response = await fetch(`${API}/api/upload/chunk`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Chunk upload failed (${response.status})`);
    }

    uploadedChunks++;
    if (onProgress) {
      onProgress(Math.round((uploadedChunks / totalChunks) * 95)); // 95% for chunks, 5% for assembly
    }
  }

  // Complete the upload — assemble chunks (GET request, zero body, params in query string)
  const params = new URLSearchParams({
    upload_id: uploadId,
    original_name: file.name,
    total_chunks: String(totalChunks),
  });
  const completeRes = await fetch(`${API}/api/upload/complete?${params}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({}));
    throw new Error(err.detail || `Assembly failed (${completeRes.status})`);
  }

  if (onProgress) onProgress(100);
  return completeRes.json();
}

// Fallback for small files (under chunk threshold)
function uploadFileSingle(token, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { reject(new Error('Invalid response from server')); }
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.detail || `Upload failed (${xhr.status})`));
        } catch { reject(new Error(`Upload failed (${xhr.status})`)); }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error — check your connection and try again')));
    xhr.addEventListener('timeout', () => reject(new Error('Upload timed out — file may be too large')));

    xhr.timeout = 600000;
    xhr.open('POST', `${API}/api/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
}

export async function seedContent(token) {
  const res = await fetch(`${API}/api/seed`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
}

// ─── Gallery API ────────────────────────────────────────────

export async function fetchGallery(page = 1, limit = 60, shuffle = true) {
  const res = await fetch(`${API}/api/gallery?page=${page}&limit=${limit}&shuffle=${shuffle}`);
  if (!res.ok) throw new Error('Failed to fetch gallery');
  return res.json();
}

export async function fetchGalleryCount() {
  const res = await fetch(`${API}/api/gallery/count`);
  if (!res.ok) throw new Error('Failed to fetch gallery count');
  return res.json();
}

export function uploadGalleryImage(token, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { reject(new Error('Invalid response')); }
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.detail || `Upload failed (${xhr.status})`));
        } catch { reject(new Error(`Upload failed (${xhr.status})`)); }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error')));
    xhr.addEventListener('timeout', () => reject(new Error('Upload timed out')));
    xhr.timeout = 120000;

    xhr.open('POST', `${API}/api/gallery/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
}

export async function deleteGalleryImage(token, imageId) {
  const res = await fetch(`${API}/api/gallery/${imageId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to delete image');
  return res.json();
}

export async function reorderGallery(token, ids) {
  const res = await fetch(`${API}/api/gallery/reorder`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error('Failed to reorder gallery');
  return res.json();
}

export async function rotateGalleryImage(token, imageId, degrees) {
  const res = await fetch(`${API}/api/gallery/${imageId}/rotate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ degrees }),
  });
  if (!res.ok) throw new Error('Failed to rotate image');
  return res.json();
}


